import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBSwitch,
  MDBTypography,
  MDBIcon,
  MDBBtn,
  MDBInput,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBListGroup,
  MDBListGroupItem,
  MDBBadge,
  MDBTextArea,
  MDBSpinner
} from 'mdb-react-ui-kit';
import { settingsService } from '../services/settingsService';
import { authService } from '../services/authService';
import { inventoryService } from '../services/inventoryService';
import { InventoryItem } from '../types';
import './Settings.css';

interface SettingsProps {
  theme: 'light' | 'dark';
  onThemeToggle: () => void;
}

const Settings: React.FC<SettingsProps> = ({ theme, onThemeToggle }) => {
  const [displayName, setDisplayName] = useState('');
  const [locationEnabled, setLocationEnabled] = useState(true);
  
  // Inventory state
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [itemName, setItemName] = useState('');
  const [itemCaliber, setItemCaliber] = useState('');
  const [itemNotes, setItemNotes] = useState('');
  const [itemIsDefault, setItemIsDefault] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<InventoryItem | null>(null);

  const loadInventory = useCallback(async () => {
    try {
      setInventoryLoading(true);
      const items = await inventoryService.getInventory();
      setInventoryItems(items);
    } catch (error) {
      console.error('Failed to load inventory:', error);
    } finally {
      setInventoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadInventory();
  }, [loadInventory]);

  const loadSettings = async () => {
    try {
      const user = authService.getUser();
      if (user) {
        setDisplayName(user.displayName || user.givenName || user.email || 'User');
      }
      const settings = await settingsService.getSettings();
      setLocationEnabled(settings.locationEnabled ?? true);
    } catch (error) {
      // Settings load failed silently
    }
  };

  const handleLocationToggle = async (enabled: boolean) => {
    setLocationEnabled(enabled);
    try {
      await settingsService.setLocationEnabled(enabled);
      
      if (enabled) {
        alert('Location Tracking Enabled\n\nNew sessions will capture location data when available.');
      } else {
        alert('Location Tracking Disabled\n\nSessions will not include location information.');
      }
    } catch (error) {
      alert('Failed to update location setting');
      setLocationEnabled(!enabled);
    }
  };

  // Inventory handlers
  const handleOpenAddItem = () => {
    setEditingItem(null);
    setItemName('');
    setItemCaliber('');
    setItemNotes('');
    setItemIsDefault(inventoryItems.length === 0);
    setInventoryModalOpen(true);
  };

  const handleOpenEditItem = (item: InventoryItem) => {
    setEditingItem(item);
    setItemName(item.name);
    setItemCaliber(item.caliber);
    setItemNotes(item.notes || '');
    setItemIsDefault(item.isDefault);
    setInventoryModalOpen(true);
  };

  const handleCloseModal = () => {
    setInventoryModalOpen(false);
    setEditingItem(null);
  };

  const handleSaveItem = async () => {
    if (!itemName.trim() || !itemCaliber.trim()) {
      alert('Please enter both name and caliber');
      return;
    }

    try {
      setIsSaving(true);
      if (editingItem) {
        await inventoryService.updateItem(editingItem.id, {
          name: itemName.trim(),
          caliber: itemCaliber.trim(),
          notes: itemNotes.trim() || undefined,
          isDefault: itemIsDefault,
        });
      } else {
        await inventoryService.createItem({
          name: itemName.trim(),
          caliber: itemCaliber.trim(),
          notes: itemNotes.trim() || undefined,
          isDefault: itemIsDefault,
        });
      }
      handleCloseModal();
      await loadInventory();
    } catch (error) {
      alert('Failed to save weapon');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDeleteConfirm = (item: InventoryItem) => {
    setDeletingItem(item);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) return;
    
    try {
      await inventoryService.deleteItem(deletingItem.id);
      setDeleteConfirmOpen(false);
      setDeletingItem(null);
      await loadInventory();
    } catch (error) {
      alert('Failed to delete weapon');
    }
  };

  const handleSetDefault = async (item: InventoryItem) => {
    try {
      await inventoryService.setDefaultItem(item.id);
      await loadInventory();
    } catch (error) {
      alert('Failed to set default weapon');
    }
  };

  return (
    <MDBContainer className="py-4">
      <MDBTypography tag="h1" className="text-center mb-4">Settings</MDBTypography>

      {/* Appearance Section */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="palette" className="me-2" />
            Appearance
          </MDBTypography>
          
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <MDBTypography className="mb-1">Dark Mode</MDBTypography>
              <MDBTypography className="text-muted small">
                {theme === 'dark' ? 'Dark theme enabled' : 'Light theme enabled'}
              </MDBTypography>
            </div>
            <MDBSwitch
              checked={theme === 'dark'}
              onChange={onThemeToggle}
            />
          </div>
        </MDBCardBody>
      </MDBCard>

      {/* User Information Section */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="user" className="me-2" />
            User Information
          </MDBTypography>
          
          <div>
            <MDBTypography className="mb-1">Username</MDBTypography>
            <MDBTypography tag="h6" className="text-primary">{displayName}</MDBTypography>
            <MDBTypography className="text-muted small">
              Your name from Google account
            </MDBTypography>
          </div>
        </MDBCardBody>
      </MDBCard>

      {/* Inventory Section */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <MDBTypography tag="h5" className="mb-0">
              <MDBIcon fas icon="crosshairs" className="me-2" />
              My Weapons
            </MDBTypography>
            <MDBBtn size="sm" onClick={handleOpenAddItem}>
              <MDBIcon fas icon="plus" className="me-1" /> Add Weapon
            </MDBBtn>
          </div>
          
          {inventoryLoading ? (
            <div className="text-center py-4">
              <MDBSpinner role="status">
                <span className="visually-hidden">Loading...</span>
              </MDBSpinner>
            </div>
          ) : inventoryItems.length === 0 ? (
            <div className="text-center py-4">
              <MDBIcon fas icon="box-open" size="3x" className="text-muted mb-3" />
              <MDBTypography className="text-muted">No weapons in inventory</MDBTypography>
              <MDBTypography className="text-muted small">
                Click "Add Weapon" to add your first weapon
              </MDBTypography>
            </div>
          ) : (
            <MDBListGroup flush>
              {inventoryItems.map((item) => (
                <MDBListGroupItem 
                  key={item.id}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div className="d-flex align-items-center">
                    <MDBIcon 
                      fas 
                      icon={item.isDefault ? 'star' : 'circle'} 
                      className={`me-3 ${item.isDefault ? 'text-warning' : 'text-muted'}`}
                    />
                    <div>
                      <div className="fw-bold">
                        {item.name}
                        {item.isDefault && (
                          <MDBBadge color="primary" className="ms-2">Default</MDBBadge>
                        )}
                      </div>
                      <small className="text-muted">
                        {item.caliber}
                        {item.notes && ` • ${item.notes}`}
                      </small>
                    </div>
                  </div>
                  <div>
                    {!item.isDefault && (
                      <MDBBtn 
                        size="sm" 
                        color="light" 
                        className="me-1"
                        onClick={() => handleSetDefault(item)}
                        title="Set as default"
                      >
                        <MDBIcon far icon="star" />
                      </MDBBtn>
                    )}
                    <MDBBtn 
                      size="sm" 
                      color="light" 
                      className="me-1"
                      onClick={() => handleOpenEditItem(item)}
                    >
                      <MDBIcon fas icon="edit" />
                    </MDBBtn>
                    <MDBBtn 
                      size="sm" 
                      color="danger"
                      onClick={() => handleOpenDeleteConfirm(item)}
                    >
                      <MDBIcon fas icon="trash" />
                    </MDBBtn>
                  </div>
                </MDBListGroupItem>
              ))}
            </MDBListGroup>
          )}
        </MDBCardBody>
      </MDBCard>

      {/* Privacy & Permissions Section */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="shield-alt" className="me-2" />
            Privacy & Permissions
          </MDBTypography>
          
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <MDBTypography className="mb-1">Track Location</MDBTypography>
              <MDBTypography className="text-muted small">
                {locationEnabled 
                  ? 'New sessions will capture location data when available'
                  : 'Sessions will not include location information'}
              </MDBTypography>
            </div>
            <MDBSwitch
              checked={locationEnabled}
              onChange={(e) => handleLocationToggle(e.target.checked)}
            />
          </div>
        </MDBCardBody>
      </MDBCard>

      {/* About Section */}
      <MDBCard>
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="info-circle" className="me-2" />
            About
          </MDBTypography>
          
          <div className="mb-3">
            <MDBTypography className="text-muted small mb-1">Version</MDBTypography>
            <MDBTypography>1.0.0</MDBTypography>
          </div>

          <div className="mb-3">
            <MDBTypography className="text-muted small mb-1">App Name</MDBTypography>
            <MDBTypography>SolidRange Web</MDBTypography>
          </div>

          <div className="mb-3">
            <MDBTypography className="text-muted small mb-1">Platform</MDBTypography>
            <MDBTypography>Web Browser</MDBTypography>
          </div>

          <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(0,0,0,0.1)' }}>
            <Link to="/privacy-policy" className="d-block mb-2 text-decoration-none">
              <MDBIcon fas icon="shield-alt" className="me-2" />
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="d-block text-decoration-none">
              <MDBIcon fas icon="file-contract" className="me-2" />
              Terms of Service
            </Link>
          </div>
        </MDBCardBody>
      </MDBCard>

      {/* Add/Edit Weapon Modal */}
      <MDBModal open={inventoryModalOpen} onClose={handleCloseModal} tabIndex={-1}>
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                {editingItem ? 'Edit Weapon' : 'Add Weapon'}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={handleCloseModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <div className="mb-3">
                <MDBInput
                  label="Name"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="e.g., Glock 17"
                />
              </div>
              <div className="mb-3">
                <MDBInput
                  label="Caliber"
                  value={itemCaliber}
                  onChange={(e) => setItemCaliber(e.target.value)}
                  placeholder="e.g., 9mm"
                />
              </div>
              <div className="mb-3">
                <MDBTextArea
                  label="Notes (optional)"
                  value={itemNotes}
                  onChange={(e) => setItemNotes(e.target.value)}
                  rows={3}
                  placeholder="Add notes about this weapon..."
                />
              </div>
              <div className="d-flex align-items-center">
                <MDBSwitch
                  checked={itemIsDefault}
                  onChange={(e) => setItemIsDefault(e.target.checked)}
                  id="defaultWeaponSwitch"
                />
                <label htmlFor="defaultWeaponSwitch" className="ms-2">
                  Set as default weapon
                </label>
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={handleCloseModal}>
                Cancel
              </MDBBtn>
              <MDBBtn onClick={handleSaveItem} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <MDBSpinner size="sm" role="status" tag="span" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* Delete Confirmation Modal */}
      <MDBModal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} tabIndex={-1}>
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Delete Weapon</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => setDeleteConfirmOpen(false)}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              Are you sure you want to delete "{deletingItem?.name}"? This action cannot be undone.
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={() => setDeleteConfirmOpen(false)}>
                Cancel
              </MDBBtn>
              <MDBBtn color="danger" onClick={handleConfirmDelete}>
                Delete
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </MDBContainer>
  );
};

export default Settings;
